import { takeLatest, call, put } from 'redux-saga/effects';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import ctfClient from 'utils/contentfulService';
import { getFirebase } from 'react-redux-firebase';
import {
  GET_ASSESSMENTS_DATA,
  GET_STORED_DATA,
  SUBMIT_ASSESSMENT_TEST_DATA,
} from './constants';
import {
  getAssessmentsDataResult,
  getStoredData,
  getStoredDataResult,
  processing,
} from './actions';

const parseScoreComponentLimits = component => {
  if (_isEmpty(component)) return null;

  return {
    name: _get(component, 'name'),
    min: _get(component, 'minValue'),
    max: _get(component, 'maxValue'),
    intervals: _get(component, 'intervals', []),
  };
};

function* getStoredDataSaga() {
  try {
    yield put(processing(true));

    const snapshot = yield getFirebase()
      .firestore()
      .collection('assessment_test_data')
      .get();
    const rows = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
    }));
    yield put(getStoredDataResult(rows));
  } catch (error) {
    yield put(getStoredDataResult([]));
  } finally {
    yield put(processing(false));
  }
}

function* getAssessmentsDataSaga() {
  try {
    yield put(processing(true));

    const response = yield call(() =>
      ctfClient.getEntries({
        content_type: 'assessments',
      }),
    );
    const rows = [];
    response.items.forEach(item => {
      const scoreComponents = Object.keys(
        _get(item, 'fields.scoreComponents', {}),
      )
        .filter(key => key !== 'overallScore')
        .map(key =>
          parseScoreComponentLimits(
            _get(item, ['fields', 'scoreComponents', key]),
          ),
        );

      rows.push({
        name: item.fields.name,
        slug: item.fields.slug,
        overallScore: parseScoreComponentLimits(
          _get(item, 'fields.scoreComponents.overallScore'),
        ),
        scoreComponents,
      });
    });
    yield put(getAssessmentsDataResult(rows));
  } catch (error) {
    yield put(getAssessmentsDataResult([]));
  } finally {
    yield put(processing(false));
  }
}

function* submitAssessmentTestDataSaga({ payload }) {
  try {
    yield put(processing(true));
    const code = yield call(generateUniqueCode);
    const { customAnswers, overallScoring, ...data } = payload;
    let finalCustomAnswers;
    if (customAnswers !== '') {
      finalCustomAnswers = {
        customAnswers: JSON.parse(customAnswers),
      };
    }
    yield getFirebase()
      .firestore()
      .collection('assessment_test_data')
      .add({
        ...data,
        ...finalCustomAnswers,
        overallScoring: overallScoring === '-1' ? null : overallScoring,
        code,
      });
  } finally {
    yield put(getStoredData());
    yield put(processing(false));
  }
}

function* isUnique(code) {
  const snapshot = yield getFirebase()
    .firestore()
    .collection('assessment_test_data')
    .where('code', '==', code)
    .get();

  return snapshot.size === 0;
}

async function generateUniqueCode() {
  const alpha = 'ABCDEFGHIJKLMNPQRSTUVWXYZ123456789';
  let code = [0, 0, 0, 0, 0, 0]
    .map(() => alpha[Math.round(Math.random() * (alpha.length - 1))])
    .join('');
  let count = 0;

  while (count < 16) {
    // eslint-disable-next-line no-await-in-loop
    if (await isUnique(code)) break;

    count += 1;
    if (count >= 16) {
      code = null;
    } else {
      code = [0, 0, 0, 0, 0, 0]
        .map(() => alpha[Math.round(Math.random() * (alpha.length - 1))])
        .join('');
    }
  }

  return code;
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_STORED_DATA, getStoredDataSaga);
  yield takeLatest(GET_ASSESSMENTS_DATA, getAssessmentsDataSaga);
  yield takeLatest(SUBMIT_ASSESSMENT_TEST_DATA, submitAssessmentTestDataSaga);
}
